/**
 * Memory API Client
 *
 * Wraps the /memory backend routes that interface with 8mem -
 * the persistent knowledge layer powering the AI PA's reasoning.
 */

import { MEMORY_API } from './api-config';
import { useAuthStore } from '../stores/authStore';

// Types

export type MemoryType = 'meeting_note' | 'commitment' | 'insight' | 'context' | 'note';

export interface MemoryItem {
  id?: string;
  collection: string;
  content: string;
  metadata: {
    type?: MemoryType;
    createdAt?: string;
    userId?: string;
    contextSender?: string;
    contextSubject?: string;
    contextTaskId?: string;
    [key: string]: string | undefined;
  };
}

export interface MemoryContext {
  person: MemoryItem[];
  commitments: MemoryItem[];
  meetings: MemoryItem[];
  insights: MemoryItem[];
  related: MemoryItem[];
}

export interface StoreMemoryPayload {
  content: string;
  type?: MemoryType;
  contextSender?: string;
  contextSubject?: string;
  contextTaskId?: string;
}

// Helpers

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

// API

export const memoryApi = {
  /**
   * Semantic search across all user knowledge in 8mem.
   */
  async search(
    query: string,
    collection?: 'email' | 'todo' | 'note' | 'capture'
  ): Promise<MemoryItem[]> {
    const url = new URL(MEMORY_API.search());
    url.searchParams.set('q', query);
    if (collection) url.searchParams.set('collection', collection);

    const res = await fetch(url.toString(), { headers: authHeaders() });
    if (!res.ok) throw new Error(`Memory search failed: ${res.status}`);

    const data = await res.json() as { results: MemoryItem[] };
    return data.results ?? [];
  },

  /**
   * Fetch structured context for a given email sender, subject, or task.
   * This is what populates the "Context" section in the Memory panel
   * when the user has an email selected.
   */
  async getContext(params: {
    sender?: string;
    subject?: string;
    taskId?: string;
  }): Promise<MemoryContext> {
    const url = new URL(MEMORY_API.context());
    if (params.sender) url.searchParams.set('sender', params.sender);
    if (params.subject) url.searchParams.set('subject', params.subject);
    if (params.taskId) url.searchParams.set('taskId', params.taskId);

    const res = await fetch(url.toString(), { headers: authHeaders() });
    if (!res.ok) throw new Error(`Memory context failed: ${res.status}`);

    const data = await res.json() as { context: MemoryContext };
    return data.context;
  },

  /**
   * Quick-capture a memory: meeting note, commitment, insight, or context.
   */
  async store(payload: StoreMemoryPayload): Promise<{ success: boolean; degraded?: boolean }> {
    const res = await fetch(MEMORY_API.store(), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Save failed' })) as { message?: string };
      throw new Error(err.message ?? 'Save failed');
    }

    return res.json() as Promise<{ success: boolean; degraded?: boolean }>;
  },
};
